import { createContext, useContext, useEffect, useState } from 'react';
import { 
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { auth, db } from '../lib/firebase/config';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { normalizeDisplayName } from '../utils/userMigrations';

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  signUp: (email: string, password: string) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Helper function to create/update user document in Firestore
  const createUserDocument = async (user: User) => {
    const userRef = doc(db, 'users', user.uid);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      // Only create if the document doesn't exist
      await setDoc(userRef, {
        email: user.email,
        displayName: user.displayName || user.email?.split('@')[0] || '',
        displayNameOriginal: user.displayName || user.email?.split('@')[0] || '',
        normalizedDisplayName: normalizeDisplayName(user.displayName || user.email?.split('@')[0] || ''),
        photoURL: user.photoURL || '',
        createdAt: serverTimestamp(),
        lastLogin: serverTimestamp(),
        solves: [],
        cubes: [],
        isPublic: true,
        preferences: {
          language: 'en',
          theme: 'system',
          isPublic: true,
          timerSettings: {
            inspection: true,
            holdDuration: 300,
          }
        }
      });

      // Set the display name in Auth if it's not set
      if (!user.displayName) {
        await updateProfile(user, {
          displayName: user.email?.split('@')[0] || ''
        });
      }
    } else {
      // Only update lastLogin and sync existing display name
      const userData = userSnap.data();
      await setDoc(userRef, {
        lastLogin: serverTimestamp(),
        // Keep existing display name or sync from auth if not set
        displayName: userData.displayName || user.displayName || user.email?.split('@')[0] || '',
        displayNameOriginal: userData.displayNameOriginal || user.displayName || user.email?.split('@')[0] || '',
        normalizedDisplayName: userData.normalizedDisplayName || normalizeDisplayName(user.displayName || user.email?.split('@')[0] || ''),
        photoURL: user.photoURL || '',
        isPublic: true,
        'preferences.isPublic': true
      }, { merge: true });

      // Sync Auth display name if it doesn't match Firestore
      if (user.displayName !== userData.displayNameOriginal) {
        await updateProfile(user, {
          displayName: userData.displayNameOriginal || userData.displayName
        });
      }
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        await createUserDocument(user);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signUp = async (email: string, password: string) => {
    const { user } = await createUserWithEmailAndPassword(auth, email, password);
    await createUserDocument(user);
  };

  const login = async (email: string, password: string) => {
    const { user } = await signInWithEmailAndPassword(auth, email, password);
    await createUserDocument(user);
  };

  const logout = async () => {
    await signOut(auth);
  };

  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    const { user } = await signInWithPopup(auth, provider);
    await createUserDocument(user);
  };

  const value = {
    currentUser,
    loading,
    signUp,
    login,
    logout,
    resetPassword,
    signInWithGoogle
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 